import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pin, X, ChevronLeft, ChevronRight, Pencil, Lock, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useShortcuts } from '../contexts/ShortcutsContext';
import usePermissions from '../hooks/usePermissions';
import { resolveShortcutIcon, shortcutColorClass, autoColor, QUICK_ACTIONS } from '../constants/shortcutTargets';

const ShortcutTile = ({ item, allowed, editing, onOpen, onRemove, onRename, onMove, isFirst, isLast }) => {
  const { t } = useTranslation();
  const Icon = allowed ? resolveShortcutIcon(item.icon) : Lock;
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onOpen}
        className={`w-full h-full text-left rounded-xl border p-3 transition-all ${
          allowed
            ? 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md active:scale-[0.98]'
            : 'border-amber-200 bg-amber-50/60'
        }`}
        data-testid={`shortcut-tile-${item.id}`}
      >
        <span className={`inline-flex w-9 h-9 items-center justify-center rounded-lg ${allowed ? shortcutColorClass(item.color) : 'bg-amber-100 text-amber-700'}`}>
          <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
        </span>
        <span className="mt-2 block text-sm font-medium text-slate-800 leading-tight line-clamp-2">{item.label}</span>
        {item.subtitle && (
          <span className="mt-0.5 block text-[11px] text-slate-500 leading-tight line-clamp-1">{item.subtitle}</span>
        )}
      </button>
      {editing && (
        <div className="absolute -top-2 -right-2 flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" className="h-6 w-6 rounded-full bg-white"
            disabled={isFirst} onClick={() => onMove(-1)} title={t('shortcuts.moveLeft')}
            data-testid={`shortcut-move-left-${item.id}`}>
            <ChevronLeft className="w-3 h-3" />
          </Button>
          <Button type="button" variant="outline" size="icon" className="h-6 w-6 rounded-full bg-white"
            disabled={isLast} onClick={() => onMove(1)} title={t('shortcuts.moveRight')}
            data-testid={`shortcut-move-right-${item.id}`}>
            <ChevronRight className="w-3 h-3" />
          </Button>
          <Button type="button" variant="outline" size="icon" className="h-6 w-6 rounded-full bg-white"
            onClick={onRename} title={t('shortcuts.rename')} data-testid={`shortcut-rename-${item.id}`}>
            <Pencil className="w-3 h-3" />
          </Button>
          <Button type="button" variant="destructive" size="icon" className="h-6 w-6 rounded-full"
            onClick={onRemove} title={t('shortcuts.remove')} data-testid={`shortcut-remove-${item.id}`}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

/** "+ Quick action" — pin a tile that opens a new-record form in one tap. */
const QuickCreateDialog = ({ open, onOpenChange, projects }) => {
  const { t } = useTranslation();
  const { canAny, can } = usePermissions();
  const { addShortcut } = useShortcuts();
  const [actionKey, setActionKey] = useState('');
  const [projectId, setProjectId] = useState('');
  const [saving, setSaving] = useState(false);

  const available = QUICK_ACTIONS.filter((a) => !a.perms?.length || canAny(...a.perms) || can(a.perms[0]));
  const action = available.find((a) => a.key === actionKey);

  const save = async () => {
    if (!action) return;
    const project = projects.find((p) => p.id === projectId);
    if (action.needsProject && !project) { toast.error(t('shortcuts.qc.pickProject')); return; }
    setSaving(true);
    try {
      await addShortcut({
        tab: action.tab,
        context: action.ctx(project?.id),
        label: t(action.labelKey),
        subtitle: project?.name || '',
        icon: action.icon,
        color: autoColor(`qc-${action.key}`),
        perms: action.perms || [],
      });
      toast.success(t('shortcuts.pinned'));
      onOpenChange(false);
      setActionKey(''); setProjectId('');
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t('shortcuts.limit') : t('shortcuts.saveFailed'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-create-dialog">
        <DialogHeader>
          <DialogTitle>{t('shortcuts.qc.title')}</DialogTitle>
          <DialogDescription>{t('shortcuts.qc.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={actionKey} onValueChange={setActionKey}>
            <SelectTrigger data-testid="quick-create-action">
              <SelectValue placeholder={t('shortcuts.qc.pickAction')} />
            </SelectTrigger>
            <SelectContent>
              {available.map((a) => (
                <SelectItem key={a.key} value={a.key}>{t(a.labelKey)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {action?.needsProject && (
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="quick-create-project">
                <SelectValue placeholder={t('shortcuts.qc.pickProject')} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('shortcuts.qc.cancel')}</Button>
          <Button onClick={save} disabled={!action || saving} data-testid="quick-create-save">
            {t('shortcuts.qc.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Grid of the signed-in user's pinned jump-offs, top of the dashboard. */
const DashboardShortcuts = ({ onNavigate, projects = [] }) => {
  const { t } = useTranslation();
  const { shortcuts, loaded, removeShortcut, renameShortcut, moveShortcut } = useShortcuts();
  const { canAny } = usePermissions();
  const [editing, setEditing] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  if (!loaded && shortcuts.length === 0) return null;

  const isAllowed = (item) => !item.perms?.length || canAny(...item.perms);

  const open = (item) => {
    if (!isAllowed(item)) {
      toast.error(t('shortcuts.noAccess'), {
        action: {
          label: t('shortcuts.remove'),
          onClick: () => removeShortcut(item.id).catch(() => {}),
        },
      });
      return;
    }
    onNavigate(item.tab, { ...(item.context || {}), _n: Date.now() });
  };

  const rename = async (item) => {
    // eslint-disable-next-line no-alert
    const next = window.prompt(t('shortcuts.namePrompt'), item.label);
    if (!next || next.trim() === item.label) return;
    try { await renameShortcut(item.id, next.trim().slice(0, 60)); } catch { toast.error(t('shortcuts.saveFailed')); }
  };

  return (
    <Card className="bg-white border-0 shadow-sm" data-testid="dashboard-shortcuts">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Pin className="w-4 h-4 text-amber-600" />
          {t('shortcuts.title')}
        </CardTitle>
        <div className="flex items-center gap-1">
        {shortcuts.length > 0 && (
          <Button variant="ghost" size="sm" className="text-xs h-7"
            onClick={() => setEditing((v) => !v)} data-testid="shortcuts-edit-toggle">
            {editing ? t('shortcuts.done') : t('shortcuts.edit')}
          </Button>
        )}
        <Button variant="ghost" size="sm" className="text-xs h-7 gap-1"
          onClick={() => setQuickOpen(true)} data-testid="shortcuts-quick-add">
          <Plus className="w-3.5 h-3.5" />
          {t('shortcuts.qc.button')}
        </Button>
        </div>
      </CardHeader>
      <CardContent>
        {shortcuts.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="shortcuts-empty">{t('shortcuts.empty')}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {shortcuts.map((item, i) => (
              <ShortcutTile
                key={item.id}
                item={item}
                allowed={isAllowed(item)}
                editing={editing}
                isFirst={i === 0}
                isLast={i === shortcuts.length - 1}
                onOpen={() => open(item)}
                onRemove={() => removeShortcut(item.id).catch(() => toast.error(t('shortcuts.saveFailed')))}
                onRename={() => rename(item)}
                onMove={(d) => moveShortcut(item.id, d)}
              />
            ))}
          </div>
        )}
      </CardContent>
      <QuickCreateDialog open={quickOpen} onOpenChange={setQuickOpen} projects={projects} />
    </Card>
  );
};

export default DashboardShortcuts;
